<template>
  <div>
    <div class="meal-planner">
      <vue-loading :active="isLoading" :is-full-page="true" />
      <div v-if="!hasBasketItems" class="header-sticky noEars my-0">
        <SwiperDate
          v-model="selectedDate"
          grouped
          selectable
          continues-elements
          :items-per-view="dayCountShown"
          compose-as-week
          :base-date="selectedDate"
          :available-dates="availableDates"
        />
        <Toolbar
          v-if="mealPlans.length > 1"
          :toolbarItems="mealPlans"
          :selected-item-id="getAcutalMealPlanId()"
          @item-click="selectMealPlan"
        />
        <ReservationList
          v-if="selectedMealPlan && selectedMealPlan.reservation"
          :reservation="selectedMealPlan.reservation" @reservation-canceled="handleReservationCanceled()"
        />
      </div>
      <!-- Sticky Header for Date, Category -->
      <div v-else class="header-sticky noEars row my-0">
        <div class="d-flex flex-grow-1 flex-nowrap px-3 def-main" ref="headerText">
          <!-- todo insert stuff -->
          <h2 v-if="selectedMealPlan" class="text-left flex-grow-1 noMargin p-1 centerContent" style="font-size: 22px">{{dateFormatted}}, {{ selectedMealPlan.description }}</h2>
          <b-button variant="secondary" class="def-inv-main button mx-1 py-1 m-1" @click="clearCart">
            {{ $t('button.cancel') }}
          </b-button>
        </div>
      </div>
          <div style="margin-bottom:4px;"></div>
      <div class="meal-planner-columns">
        <!-- MealPlan Column -->
        <div class="mealplan-column">
          <div>
            <MealPlan :key="mealPlanKey"
                      v-if="selectedDate && selectedMealPlan && !selectedMealPlan.reservation"
                      :mealPlan="selectedMealPlan"
                      :basketTile="basketTile"
                      :groupProductCount="groupProductCount"
                      :productMaxMap="productMaxMap"
                      ref="mealPlanItem"
                      @basket-tile-updated="onBasketTileUpdated"
                      @product-quantity-change="handleProductQuantityChange"
            />
            <div v-else-if="!isLoading && (!selectedMealPlan || (selectedMealPlan && !selectedMealPlan.reservation) )" class="meal-plan-noOrderAvailable">
              <h3>{{$t('text.noMealPlanOrderSelectedDate')}}</h3>
            </div>
          </div>
          <GoToBasketButton
            class="d-md-none"
            v-if="basketTile && basketTile.numberOfItems > 0"
            :product-count="basketTile ? basketTile.numberOfItems : 0"
            :formatted-total="basketTile ? basketTile.formattedTotal : 0.00"
            :hidden="!basketTile || basketTile.numberOfItems === 0"
            v-collapse:100="!basketTile || basketTile.numberOfItems === 0"
            @open-basket-dialog="showBasketDialog = true"
          />
        </div>
        <!-- Basket Column (desktop/tablet only) -->
        <div v-if="isMealPlanValid && basketTile && basketTile.numberOfItems > 0" class="basket-column d-none d-md-inline-block stickyZero">
          <!-- the style "top" can be calculated based on : element.offsetTop -->
          <Basket
            :basketTile="basketTile"
            :show-vat="false"
            :product-max-map="productMaxMap"
            @product-quantity-change="handleProductQuantityChange"
          />
          <Button icon-left="money-bill" @click="goToCheckout">{{ $t('button.checkout') }}</Button>
        </div>
      </div>
      <transition name="fade-up">
        <div v-if="showBasketDialog" class="basket-dialog-overlay basket-dialog-mobile" @click.self="showBasketDialog = false">
          <div class="basket-dialog-content basket-dialog-content-mobile">
            <Basket
              :basketTile="basketTile"
              :show-vat="false"
              :product-max-map="productMaxMap"
              @product-quantity-change="handleProductQuantityChange"
            />
            <div>
              <Button icon-left="money-bill" @click="goToCheckout">{{ $t('button.checkout') }}</Button>
              <Button icon-left="close" secondary @click="showBasketDialog = false">{{ $t('button.close') }}</Button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import MealPlan from "@/components/page-components/MealPlanner/MealPlan.vue";
import SwiperDate from "@/components/layout-components/SwiperDate.vue";
import Toolbar from "@/components/layout-components/Toolbar.vue";
import { productService } from "@/services/api/productService";
import Utils from "@/utils/utils";
import ReservationList from "@/components/page-components/MealPlanner/ReservationList.vue";
import GoToBasketButton from "@/components/sub-components/GoToBasketButton.vue";
import {shopService} from "@/services/api/shopService";
import {basketService} from "@/services/api/basketService";
import {useShopStore} from "@/stores/shop";
import {useAppStore} from "@/stores/app";
import basketMixin from "@/components/mixins/basketMixin";
import Basket from "@/components/sub-components/Basket/Basket.vue";
import {OrderTypes} from "@/constants/constants";
import { isMealPlanValid as isMealPlanValidUtil } from "@/utils/mealPlanUtils";
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import Button from "@/components/barrier-free-template-components/Button.vue";
import {orderService} from "@/services/api/orderService";

export default {
  mixins: [basketMixin],
  components: {
    Button,
    Basket,
    ReservationList,
    SwiperDate,
    Toolbar,
    MealPlan,
    GoToBasketButton,
    VueLoading,
  },
  data() {
    return {
      selectedDate: this.appStore.getSelectedMealPlanDate
        ? new Date(this.appStore.getSelectedMealPlanDate)
        : new Date(),
      selectedMealPlan: null,
      mealPlans: [],
      isLoading: false,
      mealPlanKey: 0,
      basketTile: null,
      groupProductCount: {},
      showBasketDialog: false,
      availableDates: [], // Store available dates for SwiperDate
    };
  },
  inject:['setPageMetaTitle'],
  setup() {
    const shopStore = useShopStore();
    const appStore = useAppStore();

    return {
      shopStore,
      appStore,
    };
  },
  updated(){
    Array.from(document.querySelectorAll(".stickyZero"))
      .forEach(function(e) { // give CSS the value. It does not have that.
        e.style.top = e.parentElement.offsetTop + "px"; // important with offsetTop, the element (e) can not have a margin-top!
      });
  },
  computed:{
    dateFormatted(){
      return this.formatterInstanceFullDate.format(this.selectedDate) // for me : '01.03.21, 14:15'
    },
    dayCountShown(){
      return window.config.MEAL_PLAN.weekDays || 5;
    },
    formatterInstanceFullDate() {
      return Intl.DateTimeFormat(this.$i18n.locale.replace('_', '-'), {formatMatcher: "best fit", weekday: "short", month: "short", day: "numeric"});
    },
    hasBasketItems() {
      return this.basketTile && this.basketTile.numberOfItems > 0;
    },
    isMealPlanValid() {
      return isMealPlanValidUtil(this.selectedMealPlan);
    },
    productMaxMap() {
      // Map of basket product id (unique per basket item) to max value
      const map = {};
      if (this.basketTile && this.basketTile.basketItems && this.selectedMealPlan && this.selectedMealPlan.groups) {
        this.basketTile.basketItems.forEach(currentItem => {
          let groupId = null;
          if (currentItem.extendedInfos) {
            try {
              const ext = typeof currentItem.extendedInfos === 'string' ? JSON.parse(currentItem.extendedInfos) : currentItem.extendedInfos;
              groupId = ext?.mealPlan?.groupId || currentItem.groupId;
            } catch {
              groupId = currentItem.groupId;
            }
          } else {
            groupId = currentItem.groupId;
          }
          // Find group max
          const group = this.selectedMealPlan.groups.find(g => g.id === groupId);
          const groupMax = group?.maxProducts || 0;
          // Sum all other items in this group (excluding this item)
          let sumOther = 0;
          this.basketTile.basketItems.forEach(item => {
            let itemGroupId = null;
            if (item.extendedInfos) {
              try {
                const ext = typeof item.extendedInfos === 'string' ? JSON.parse(item.extendedInfos) : item.extendedInfos;
                itemGroupId = ext?.mealPlan?.groupId || item.groupId;
              } catch {
                itemGroupId = item.groupId;
              }
            } else {
              itemGroupId = item.groupId;
            }
            if (item.id !== currentItem.id && itemGroupId === groupId) {
              sumOther += Number(item.quantity) || 0;
            }
          });
          // The max for this item is groupMax - sumOther
          map[currentItem.id] = Math.max(0, groupMax - sumOther);
        });
      }
      return map;
    },
  },
  async created() {
    const wondCode = this.shopStore.getWondCode;
    if (wondCode) {
      await shopService.selectShopByWondCode(wondCode);
    } else {
      await shopService.selectShopById(this.shopStore.getShopId);
    }
    await this.setPageMetaTitle(this.shopStore.getShopName);

    await this.fetchTimeSlots();
    await this.fetchMealPlans(this.selectedDate);

    // Initialize groupProductCount on load
    this.updateGroupProductCount(this.basketTile);
  },
  mounted() {
    this.$root.$on('trigger-download', this.goToDownloads);
  },
  beforeDestroy() {
  },
  methods: {
    goToCheckout() {
      this.$navigateToMealPlanCheckout();
    },
    goToDownloads() {
      this.$navigateToMealPlanDownloads();
    },
    forceRerender() {
      this.mealPlanKey += 1;
    },
    async onMealPlanSelect(mealPlan) {
      this.selectedMealPlan = mealPlan;
      // Only fetch basketTile if there is no reservation for this meal plan
      if (!mealPlan.reservation) {
        const options = {
          mealPlanId: mealPlan.id,
          reservationDateTime: mealPlan.reservationDateTime,
        };
        this.basketTile = await this.getBasketTile(OrderTypes.DINE_IN, options);
      } else {
        this.basketTile = null;
      }
    },
    handleReservationCanceled() {
      this.selectedMealPlan.reservation = null;
      this.makeToast("success", this.$t("notification.cancelOrderSuccessful"));
    },
    getAcutalMealPlanId() {
      if (this.selectedMealPlan) {
        return this.selectedMealPlan.id;
      }

      if (!this.appStore.currentMealPlanOrderId) {
        return null;
      }

      // Fallback: Select the first available meal plan
      if (this.mealPlans.length > 0) {
        return this.mealPlans[0].id;
      }

      return null; // No valid meal plan available
    },
    async fetchMealPlans(date) {
      try {
        let mealPlanIdToSelect = this.getAcutalMealPlanId();

        this.isLoading = true;
        const result = await productService.getMealPlans(Utils.getStartOfDate(date), Utils.getEndOfDate(date));
        this.mealPlans = result.mealPlans || [];

        if (this.mealPlans.length > 0) {
          if(mealPlanIdToSelect > 0)
          {
            const selectedMealPlan = this.mealPlans.find(plan => plan.id === mealPlanIdToSelect);
            if(selectedMealPlan)
            {
              await this.onMealPlanSelect(selectedMealPlan)
            }
            else
            {
              await this.onMealPlanSelect(this.mealPlans[0]);
            }
          }
          else
          {
            await this.onMealPlanSelect(this.mealPlans[0]);
          }
        } else {
          this.selectedMealPlan = null; // No meal plans available
        }
      } catch (error) {
        console.error("Failed to fetch meal plans:", error);
      } finally {
        this.isLoading = false;
      }
    },
    selectMealPlan(index) {
      // Ensure the index is valid
      if (index >= 0 && index < this.mealPlans.length) {
        this.onMealPlanSelect(this.mealPlans[index]);
      } else {
        console.error("Invalid meal plan index:", index);
      }
    },
    async clearCart(){
      await basketService.emptyBasket();
      this.forceRerender();
      // Refresh basketTile after clearing cart
      this.basketTile = await this.getBasketTile();
    },
    onBasketTileUpdated(newBasketTile) {
      this.basketTile = newBasketTile;
      this.updateGroupProductCount(newBasketTile);
    },
    updateGroupProductCount(basketTile) {
      // Reset groupProductCount
      const groupCounts = {};
      if (basketTile && basketTile.basketItems) {
        basketTile.basketItems.forEach(item => {
          let groupId = null;
          // Try to get groupId from extendedInfos.mealPlan.groupId
          if (item.extendedInfos) {
            try {
              const ext = typeof item.extendedInfos === "string"
                ? JSON.parse(item.extendedInfos)
                : item.extendedInfos;
              groupId = ext?.mealPlan?.groupId;
            } catch {
              // fallback
              groupId = item.groupId;
            }
          } else {
            groupId = item.groupId;
          }
          if (!groupId) return;
          const quantity = Number(item.quantity) || 0;
          // Use composite key: prodId + optionText
          const uniqueKey = `${item.prodId}_${item.optionText || ''}`;
          if (!groupCounts[groupId]) groupCounts[groupId] = {};
          if (!groupCounts[groupId][uniqueKey]) groupCounts[groupId][uniqueKey] = 0;
          groupCounts[groupId][uniqueKey] += quantity;
        });
      }
      this.groupProductCount = groupCounts;
    },
    async handleProductQuantityChange(basketId, newQuantity) {
      // Find the current quantity in the basket
      const currentItem = this.basketTile?.basketItems?.find(item => item.id === basketId);

      // Extract groupId from extendedInfos if present, else fallback to groupId
      let groupId = null;
      if (currentItem) {
        if (currentItem.extendedInfos) {
          try {
            const ext = typeof currentItem.extendedInfos === 'string' ? JSON.parse(currentItem.extendedInfos) : currentItem.extendedInfos;
            groupId = ext?.mealPlan?.groupId || currentItem.groupId;
          } catch {
            groupId = currentItem.groupId;
          }
        } else {
          groupId = currentItem.groupId;
        }
      }
      const mealPlan = {
        mealPlanId: this.selectedMealPlan?.id,
        reservationDateTime: this.selectedMealPlan.reservationDateTime,
        groupId: groupId
      };

      let result;
      try {
        result = await basketService.editBasket(basketId, newQuantity, OrderTypes.DINE_IN, mealPlan);
        if (result && result.basketTile) {
          this.onBasketTileUpdated(result.basketTile);
        }
      } catch (e) {
        // Optionally handle error
      }
    },
    async fetchTimeSlots() {
      this.isLoading = true;
      try {
        const result = await orderService.getTimeSlots();
        if (!result.availabilityList || !result.orderTypes) {
          return;
        }

        // Set availableDates as array of Date objects
        this.availableDates = result.availabilityList
          .filter((entry) => !entry.closed)
          .map((entry) => new Date(entry.date));

        console.log("Available Dates:", this.availableDates);
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    selectedDate: {
      immediate: false,
      async handler(newDate, oldDate) {
        if (newDate instanceof Date && !isNaN(newDate)) {
          // Save to store
          this.appStore.setSelectedMealPlanDate(newDate);
          if (!oldDate || !(oldDate instanceof Date) || isNaN(oldDate) || newDate.getTime() !== oldDate.getTime()) {
            await this.fetchMealPlans(newDate);  // Fetch meal plans whenever date changes
            console.log("date changed");
          }
        }
      },
    },
    basketTile: {
      immediate: true,
      handler(newBasketTile) {
        this.updateGroupProductCount(newBasketTile);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/css/customizer';

.meal-planner {
  text-align: center;
}

.meal-planner-columns {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  gap: 24px;
}

.mealplan-column {
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
}

.basket-column {
  width: 350px;
  max-width: 100vw;
  max-height: 80vh;
  overflow-y: auto;
  position: sticky;
  border-radius: 12px 12px 0 0; // tl, tr, br, bl
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.meal-plan-noOrderAvailable {
  color: custom-chain(mealplan-noorder-color,red);
  padding-top: 4%;
}

.fade-up-enter-active, .fade-up-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-up-enter-from, .fade-up-leave-to {
  opacity: 0;
  transform: translateY(40px);
}
.fade-up-enter-to, .fade-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.basket-dialog-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
}
.basket-dialog-content {
  background: #fff;
  border-radius: 12px 12px 0 0;
  width: 100%;
  max-width: 480px;
  margin-bottom: 0;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  box-shadow: 0 -2px 16px rgba(0,0,0,0.12);
}

@media (max-width: 768px) {
  .basket-dialog-overlay.basket-dialog-mobile {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0,0,0,0.3);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 1000;
    transition: none;
  }
  .basket-dialog-content.basket-dialog-content-mobile {
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
    margin: 0;
    padding: 2rem 1rem 1rem 1rem;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
}
</style>
